/**
 * Pure helpers behind the default gate (TRA-1368), kept out of the
 * component so they are testable without a DOM.
 *
 * A run's held-out eval compares its served step with the default model's
 * latest night, over the held-out tests both scored from one bundle, and
 * writes a verdict into run.json. Staff make an eligible model the team's
 * default; the api refuses a blocked one.
 */

import type { TrainingDefaultGate, TrainingHeldOutEval } from '@/api/generated/model'

/** What the gate block shows: its chip and which actions it offers. */
export type GateState = 'default' | 'eligible' | 'blocked' | 'unjudged'

export const GATE_LABEL: Record<GateState, string> = {
  default: 'Default',
  eligible: 'Eligible',
  blocked: 'Blocked',
  unjudged: 'No verdict',
}

export const GATE_VARIANT: Record<GateState, 'success' | 'secondary' | 'warning' | 'outline'> = {
  default: 'success',
  eligible: 'secondary',
  blocked: 'warning',
  unjudged: 'outline',
}

/**
 * Whether a team model is the one the eval's served step is registered as.
 * The gate names that row by id, which a rename of the model leaves
 * alone. An eval scored before the gate existed names no row, only the
 * slug the step was registered under, and that slug is one model's in a
 * team.
 */
export function isServedModel(
  model: { id: string; slug: string },
  heldOut: TrainingHeldOutEval,
): boolean {
  const gate = heldOut.gate ?? null
  return gate === null ? model.slug === heldOut.model_slug : model.id === gate.model_id
}

/** A model that is already the default reads as that, whatever its eval found. */
export function gateState(gate: TrainingDefaultGate | null, isDefault: boolean): GateState {
  if (isDefault) return 'default'
  if (gate === null) return 'unjudged'
  return gate.verdict
}

function testCount(count: number): string {
  return count === 1 ? '1 held-out test' : `${count.toLocaleString()} held-out tests`
}

/** The verdict in sentences, one fact each: the comparison first, then what blocks it. */
export function gateLines(gate: TrainingDefaultGate | null): string[] {
  if (gate === null) {
    return ['This eval was scored before the default gate existed.']
  }
  const lines: string[] = []
  if (gate.score !== null && gate.baseline !== null) {
    lines.push(
      `It scored ${gate.score.toLocaleString()} on ${testCount(gate.compared)} and the default model scored ${gate.baseline.toLocaleString()}.`,
    )
  }
  if (gate.not_scored.length > 0) {
    const verb = gate.not_scored.length === 1 ? 'was' : 'were'
    lines.push(`${testCount(gate.not_scored.length)} ${verb} not scored.`)
  }
  if (gate.blocked_by.includes('no_baseline')) {
    lines.push('There is no nightly score of the default model to compare it with.')
  }
  return lines
}
